import React, { Component, useMemo } from "react";
import client from "@culturehq/client";
import { Loader, PlainButton, Success } from "@culturehq/components";

import { useProfile } from "app/context";
import { errorService, t, Trans } from "app/utils";

import PageHeader from "components/display/PageHeader";

import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import GoogleIntegrationForm from "./GoogleIntegrationForm";

const HELP_URL = "http://help.culturehq.com/for-company-administrators/culturehq-and-google-calendar-integration";

const GoogleConfigured = ({ webhookUrl, integration: { verification }, onDelete }) => {
  const verificationUrl = `${webhookUrl}/${verification}.html`;

  return (
    <>
      <Success>
        <Trans>
          The integration has been configured successfully. Google web ownership
          has been verified at
        </Trans>{" "}
        <a href={verificationUrl}><code>{verificationUrl}</code></a>.
      </Success>
      <p>
        <Trans>You may click here to </Trans>
        <ChqModalConfirmDelete
          onAccept={onDelete}
          trigger={onTrigger => (
            <PlainButton onClick={onTrigger}>
              <Trans>delete your integration.</Trans>
            </PlainButton>
          )}
        >
          <Trans>
            By clicking the delete button below, you will remove the integration
            with Google. This will remove the ability for any events to be
            synchronized with Google calendar.
          </Trans>
        </ChqModalConfirmDelete>
      </p>
    </>
  );
};

const GoogleNotConfigured = ({ webhookUrl, onCreate }) => (
  <>
    <p>
      <Trans>
        To learn more about how to configure an integration and what enabling
        such an integration will do,
      </Trans>{" "}
      <a href={HELP_URL}><Trans>click this link.</Trans></a>
    </p>
    <GoogleIntegrationForm webhookUrl={webhookUrl} onCreate={onCreate} />
  </>
);

class GoogleIntegrationContext extends Component {
  state = { loading: true, integration: null };

  componentDidMount() {
    this.componentIsMounted = true;
    return this.fetchIntegration();
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  handleCreate = async ({ verification, credentials }) => {
    this.setState({ loading: true });

    const { integration } = await client.createGoogleIntegration({ verification, credentials });

    this.mountedSetState({ loading: false, integration });
  };

  handleDelete = async () => {
    this.setState({ loading: true });

    await client.deleteGoogleIntegration();

    this.setState({ loading: false, integration: null });
  };

  mountedSetState = mutation => {
    if (this.componentIsMounted) {
      this.setState(mutation);
    }
  };

  async fetchIntegration() {
    try {
      const { integration } = await client.getGoogleIntegration();

      this.mountedSetState({ loading: false, integration });
    } catch (error) {
      if (error.status !== 404) {
        errorService.error(error);
      } else {
        this.mountedSetState({ loading: false });
      }
    }
  }

  render() {
    const { webhookUrl } = this.props;
    const { loading, integration } = this.state;

    return (
      <>
        <PageHeader title={t("Google Integration")}>
          <Trans>
            Here you can configure an integration with Google Calendar. The
            integration will synchronize events in CultureHQ with the calendars
            of the users configured from within your Google organization.
          </Trans>
        </PageHeader>
        <Loader loading={loading}>
          {
            integration
              ? (
                <GoogleConfigured
                  webhookUrl={webhookUrl}
                  integration={integration}
                  onDelete={this.handleDelete}
                />
              )
              : <GoogleNotConfigured webhookUrl={webhookUrl} onCreate={this.handleCreate} />
          }
        </Loader>
      </>
    );
  }
}

const GoogleIntegration = () => {
  const { profile } = useProfile();

  const webhookUrl = useMemo(
    () => {
      const getSubdomain = subdomain => {
        if (subdomain === "platform-staging") {
          return "api-staging";
        }

        if (subdomain === "eu-platform") {
          return "uk-api";
        }

        return "api";
      };

      const param = profile.organization.name.toLowerCase().replace(/\s/g, "-");
      const subdomain = window.location.host.split(".")[0];
      return `https://${getSubdomain(subdomain)}.culturehq.com/webhooks/google/calendars/${param}`;
    },
    [profile.organization.name]
  );

  return <GoogleIntegrationContext webhookUrl={webhookUrl} />;
};

export default GoogleIntegration;
